'use client'

import * as React from 'react'
import type { ButtonSize, ButtonVariant } from '@repo/studio-design-system-ui-button'
import { MediaCarousel } from '@repo/studio-design-system-ui-media-carousel'
import type { ScrollButtonRenderProps } from '@repo/studio-design-system-ui-media-carousel'
import { tw } from 'utils/classnames'

export interface PromoCardWithCarouselLinkProps {
  children: React.ReactNode
}

export interface PromoCardWithCarouselImage {
  src: string
}

export interface PromoCardWithCarouselImageProps {
  src: string
  className: string
}

export type PromoCardWithCarouselLinkRenderer = (
  props: PromoCardWithCarouselLinkProps,
) => React.ReactElement
export type PromoCardWithCarouselImageRenderer = (
  props: PromoCardWithCarouselImageProps,
) => React.ReactNode

export interface PromoCardCarouselConfig {
  className: string
  itemWidth: number
  height: number
  buttonHeight: number
}

interface InternalCarouselItem extends PromoCardWithCarouselImage {
  imageClassName?: string
  renderImage: PromoCardWithCarouselImageRenderer
  renderLink: PromoCardWithCarouselLinkRenderer
}

export interface PromoCardWithCarouselProps {
  images: PromoCardWithCarouselImage[]
  promoCard: React.ReactNode
  renderLink: PromoCardWithCarouselLinkRenderer
  renderImage?: PromoCardWithCarouselImageRenderer
  className?: string
  layoutClassName?: string
  imageClassName?: string
  gap?: number
  shadowColor?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  renderCarouselButton?: (props: ScrollButtonRenderProps) => React.ReactNode
  mobileCarouselConfig?: Partial<PromoCardCarouselConfig>
  desktopCarouselConfig?: Partial<PromoCardCarouselConfig>
}

const DEFAULT_LAYOUT_CLASSNAME = 'grid grid-cols-1 gap-1 @xl:grid-cols-[380px_1fr] items-center'

const DEFAULT_MOBILE_CAROUSEL_CONFIG: PromoCardCarouselConfig = {
  className: '@4xl:hidden h-full',
  itemWidth: 200,
  height: 300,
  buttonHeight: 300,
}

const DEFAULT_DESKTOP_CAROUSEL_CONFIG: PromoCardCarouselConfig = {
  className: 'hidden @4xl:flex h-full items-center',
  itemWidth: 232,
  height: 348,
  buttonHeight: 348,
}

const NOOP = () => {}

function defaultRenderImage({ src, className }: PromoCardWithCarouselImageProps) {
  return <img src={src} alt="" className={className} loading="lazy" />
}

function CarouselItem({ item }: { item: InternalCarouselItem; realIndex: number }) {
  return (
    <div className="relative size-full overflow-hidden rounded-2">
      {item.renderLink({
        children: item.renderImage({
          src: item.src,
          className: tw('size-full bg-gray-800 object-cover', item.imageClassName),
        }),
      })}
    </div>
  )
}

export function PromoCardWithCarousel({
  images,
  promoCard,
  renderLink,
  renderImage = defaultRenderImage,
  className,
  layoutClassName = DEFAULT_LAYOUT_CLASSNAME,
  imageClassName,
  gap = 8,
  shadowColor = 'text-theme_semantic_surface_scrim_content_edge_base-start w-12',
  buttonVariant,
  buttonSize,
  renderCarouselButton,
  mobileCarouselConfig,
  desktopCarouselConfig,
}: PromoCardWithCarouselProps) {
  const galleryImages = React.useMemo(
    () =>
      images.map(item => ({
        ...item,
        imageClassName,
        renderImage,
        renderLink,
      })),
    [imageClassName, images, renderImage, renderLink],
  )

  const mobileCarousel = {
    ...DEFAULT_MOBILE_CAROUSEL_CONFIG,
    ...mobileCarouselConfig,
  }
  const desktopCarousel = {
    ...DEFAULT_DESKTOP_CAROUSEL_CONFIG,
    ...desktopCarouselConfig,
  }

  return (
    <div className={tw('@container', className)}>
      <div className={layoutClassName}>
        {promoCard}
        <div className="relative overflow-hidden">
          <MediaCarousel
            className={mobileCarousel.className}
            shadowColor={shadowColor}
            items={galleryImages}
            ItemComponent={CarouselItem}
            itemWidth={mobileCarousel.itemWidth}
            height={mobileCarousel.height}
            buttonHeight={mobileCarousel.buttonHeight}
            gap={gap}
            hasNextPage={false}
            isFetchingNextPage={false}
            fetchNextPage={NOOP}
            buttonVariant={buttonVariant}
            buttonSize={buttonSize}
            renderScrollButton={renderCarouselButton}
          />
          <MediaCarousel
            className={desktopCarousel.className}
            shadowColor={shadowColor}
            items={galleryImages}
            ItemComponent={CarouselItem}
            itemWidth={desktopCarousel.itemWidth}
            height={desktopCarousel.height}
            buttonHeight={desktopCarousel.buttonHeight}
            gap={gap}
            hasNextPage={false}
            isFetchingNextPage={false}
            fetchNextPage={NOOP}
            buttonVariant={buttonVariant}
            buttonSize={buttonSize}
            renderScrollButton={renderCarouselButton}
          />
        </div>
      </div>
    </div>
  )
}
